import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { ModalConfirm } from "tamtam-components";
import { toast } from "react-toastify";
import classnames from "classnames";

import Loader from "../../common/Loader";
import { Pagination } from "../../";
import { StatusButton } from "../../";
import _ from "../../../i18n";
import {
  emailReminder,
  deleteGoPeopleClient,
  associateDelegationToFolders,
  getGoPeopleClients,
} from "../../../api";
import { LEGAL_FORM } from "../../../config";
import {
  formatPhone,
  formatHidePhone,
  obscureEmail,
  mapDelegationType,
} from "../../../utils";
import styles from "./Client.module.scss";
import ModalDelegationType from "./ModalDelegationType";
import ModalDelegation from "./ModalDelegation";
import ModalContact from "./ModalContact";

const ClientList = (props) => {
  const prevSearchRef = useRef();
  prevSearchRef.current = "";
  const queryClient = useQueryClient();
  const auth = useSelector((state) => state.auth);
  const language = useSelector((state) => state.params.language);
  const [sort, setSort] = useState({
    property: "name",
    dir: "asc",
  });
  const [dropDownId, setDropDownId] = useState(null);
  const [search, setSearch] = useState("");
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentDeleteItem, setCurrentDeleteItem] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [openDelegationTypeModal, setOpenDelegationTypeModal] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openDelegationModal, setOpenDelegationModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [hasDelegationItems, setHasDelegationItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [foundResult, setFoundResult] = useState([]);
  const [totalFoundResult, setTotalFoundResult] = useState([]);
  const [searchContacts, setSearchContacts] = useState([]);
  const [dataGoPeople, setDataGoPeople] = useState([]);
  const [nbrResult, setNbrResult] = useState(0);
  const { token, jwtToken, navCommunity, goPeopleCommunityId } = auth;
  const LIMIT = 10;

  const fetchGoPeopleClients = async () => {
    try {
      const clientsResponce = await getGoPeopleClients(
        goPeopleCommunityId,
        jwtToken,
        LIMIT,
        currentPage
      );
      setDataGoPeople({
        ...clientsResponce.data.data[0],
        folders: prepareDataGoPeople(clientsResponce.data.data[0]),
      });
      setNbrResult(clientsResponce.data.data[0].total_folders);
    } catch (error) {
      console.warn("GoPeople API failed, trying TTP API:", error);
      // Tu peux décommenter le fallback ici si besoin
      // userResponse = await fetchTTPUser(
      //   authContext.ttpUserId || data.id,
      //   authContext.ttpAccessToken || data.token,
      //   1
      // );
    }
  };

  let { isLoading, data, refetch } = useQuery(
    ["listClients", token, sort, currentPage, goPeopleCommunityId],
    async () => {
      if (token && search === "") {
        try {
          const clientsResponce = await getGoPeopleClients(
            goPeopleCommunityId,
            jwtToken,
            LIMIT,
            currentPage
          );
          setDataGoPeople({
            ...clientsResponce.data.data[0],
            folders: prepareDataGoPeople(clientsResponce.data.data[0]),
          });
          setNbrResult(clientsResponce.data.data[0].total_folders);
          return prepareDataGoPeople(clientsResponce.data.data[0]);
        } catch (e) {
          return null;
        }
      }
    }
  );

  useEffect(() => {
    fetchGoPeopleClients();
    if (props.refreshList) {
      queryClient.invalidateQueries("listClients");
      props.setRefreshList(false);
    }
  }, [props.refreshList]);

  useEffect(() => {
    if (search !== "") {
      handleSearchChange(search, currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (prevSearchRef.current === "") {
      setCurrentPage(1);
    }
  }, [search]);

  const prepareDataGoPeople = (data) => {
    return data && data.folders && data.folders.length > 0
      ? data.folders.map((item) => {
          let delegations = [];
          let delgUuid = [];
          data.delegations
            .filter((deleg) => deleg.folder_uuid === item.uuid)
            .map((deg) => {
              if (!delgUuid.includes(deg.contact_uuid)) {
                delegations.push(deg);
                delgUuid.push(deg.contact_uuid);
              }
            });
          let legalRepresentative = data.contactsFolderRole?.filter(
            (cont) =>
              cont.role_type === "LEGAL_REPRESENTATIVE" &&
              cont.folder_id === item.uuid
          );
          let contacts = data.contactsFolderRole
            ?.filter((cont) => cont.folder_id === item.uuid)
            .map((cont) => {
              let infos = data.contacts.filter(
                (contact) => contact.uuid === cont.contact_id
              );
              const c = infos.length ? infos[0] : null;
              return {
                ...cont,
                emails: c ? c.emails : [],
                first_name: c ? (c.first_name ?? c.firstname ?? null) : null,
                last_name: c ? (c.last_name ?? c.lastname ?? null) : null,
              };
            });
          return { ...item, delegations, legalRepresentative, contacts };
        })
      : [];
  };

  const handleSearchChange = async (value, page) => {
    setSearch(value);
    prevSearchRef.current = value;
    if (value === "") {
      return;
    }
    setUploading(true);
    // Same endpoint and shape as the list (folders-data), now with the server-side
    // ?q= folder-name search, so search rows go through the same prepareDataGoPeople.
    const clientsResponce = await getGoPeopleClients(
      goPeopleCommunityId,
      jwtToken,
      LIMIT,
      page,
      value
    );
    const foldersData = clientsResponce.data.data[0];
    setSearchContacts(foldersData.contacts ?? []);
    setFoundResult(prepareDataGoPeople(foldersData));
    setTotalFoundResult(foldersData.total_folders);
    setUploading(false);
  };

  const toggleDropDown = (e, userId) => {
    e.stopPropagation();
    dropDownId === userId ? setDropDownId(null) : setDropDownId(userId);
  };

  const handleDeleteAction = () => {
    if (currentDeleteItem) {
      setDeleting(true);
      deleteGoPeopleClient(
        jwtToken,
        goPeopleCommunityId,
        currentDeleteItem.uuid
      )
        .then((resp) => {
          if (search !== "") {
            handleSearchChange(search, currentPage);
          } else {
            queryClient.invalidateQueries("listClients");
          }
          setTimeout(() => {
            toast.success(_("successfully_deleted"));
            setDeleting(false);
            setIsOpenDeleteModal(false);
            props.afterDelete();
          }, 1000);
        })
        .catch((e) => {
          setDeleting(false);
        });
    }
  };

  const getActions = (item) => {
    return item.adminisId ? (
      <div className={styles.lockIcon}>
        <i className="icon-ttp-locked"></i>
      </div>
    ) : (
      <div className={styles.dropDownContainer}>
        <span
          className={`${styles.cellIcon} ${
            dropDownId === item.uuid && styles.active
          }`}
        >
          <span
            className={`${
              dropDownId === item.uuid
                ? "icon-ttp-close"
                : "icon-ttp-three-dots"
            } ${styles.settingsIcon}`}
            onClick={(e) => toggleDropDown(e, item.uuid)}
          />
        </span>
        <ul
          className={`${styles.menuDropdown} ${
            dropDownId === item.uuid && styles.show
          }`}
        >
          {item.uaClientStatus !== "PENDING" && (
            <li
              onClick={(e) => {
                toggleDropDown(e, item.uuid);
                props.setCurrentItem(item);
                props.setSideBar("CLIENT_FORM");
              }}
            >
              {_("edit")}
            </li>
          )}
          <li
            onClick={() => {
              setCurrentDeleteItem(item);
              setIsOpenDeleteModal(true);
            }}
          >
            {_("delete")}
          </li>
        </ul>
      </div>
    );
  };

  const handleClick = (fiduciaryId, userId) => {
    setIsSending(true);
    let data = {
      token,
      language,
      uid: userId,
      fid: fiduciaryId,
      type: "CLIENT",
    };
    emailReminder(data)
      .then(() => {
        toast.success(_("reminder_email_sent_successfully"));
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      })
      .finally(() => setIsSending(false));
  };

  const handleSelectItem = (item) => {
    let hasDelegation = item.delegations.length > 0 ? true : false;
    if (selectedItems.includes(item.id)) {
      setSelectedItems(selectedItems.filter((id) => id !== item.id));
    } else {
      if (selectedItems.length > 0) {
        selectedItems.forEach((id) => {
          if (hasDelegationItems.includes(id)) {
            hasDelegation = true;
          }
        });
        if (!hasDelegation) setSelectedItems([...selectedItems, item.id]);
      } else {
        setSelectedItems([item.id]);
      }
    }
  };

  const refreshAssociation = async () => {
    const currentYear = new Date().getFullYear();
    await associateDelegationToFolders(
      auth.token,
      navCommunity?.id,
      currentYear
    )
      .then((resp) => {
        queryClient.invalidateQueries("listClients");
        setTimeout(() => {
          toast.success(_("successfully_refreshed"));
        }, 1000);
      })
      .catch((e) => {
        console.log("Error");
      });
  };

  const renderContactFullName = (uuid) => {
    const contactsList =
      search !== "" ? searchContacts : (dataGoPeople.contacts ?? []);
    let infos = contactsList.filter((contact) => contact.uuid === uuid);
    if (!infos.length) return "";
    const c = infos[0];
    return `${c.first_name ?? c.firstname ?? ""} ${c.last_name ?? c.lastname ?? ""}`.trim();
  };

  const renderData = (data) => {
    let collabData =
      data && data.length > 0
        ? data.map((item) => {
            return (
              <tr key={item.uuid}>
                <td>
                  <div className={styles.action}>
                    {/* <input
                        type="checkbox"
                        checked={selectedItems.includes(item.uuid)}
                        onChange={() => {
                          handleSelectItem(item);
                        }}
                      />*/}
                    {getActions(item)}
                  </div>
                </td>
                <td>
                  {item.delegations?.length > 0 &&
                    item.delegations.map((d) => (
                      <span className={styles.delegation} key={d.id}>
                        {`${mapDelegationType(d.delegation_type)}: ${renderContactFullName(d.contact_uuid)}`}
                      </span>
                    ))}
                </td>
                <td>
                  {item.folder_name} <br />
                  {item.uen}
                </td>
                <td>
                  {LEGAL_FORM[item.legal_form]
                    ? LEGAL_FORM[item.legal_form]
                    : null}
                </td>
                <td>{item.languages?.join("-")}</td>
                <td>
                  {item.legalRepresentative?.length > 0 &&
                    item.legalRepresentative.map((l) => (
                      <span>
                        {renderContactFullName(l.contact_id)}
                        <br />
                      </span>
                    ))}
                </td>
                <td>
                  {item.contacts?.length > 0 &&
                    item.contacts.map((c, i) => (
                      <span className={styles.delegation} key={`c-${i}`}>
                        {`${c.first_name} ${c.last_name}`}
                      </span>
                    ))}
                </td>
                {/* <td>
                  {item.legalRepresentative.mainPhone &&
                    (item.uaClientStatus !== "PENDING"
                      ? formatPhone(item.legalRepresentative.mainPhone)
                      : formatHidePhone(item.legalRepresentative.mainPhone))}
                </td> */}

                {/* <td>
                    <StatusButton
                      // status={item.uaClientStatus}
                      handleClick={() =>
                        handleClick(item.id, item.legalRepresentative.id)
                      }
                    />
                  </td> */}
              </tr>
            );
          })
        : [];

    if (collabData?.length == 0) {
      return (
        <tr>
          <td colSpan={6} className={styles.centerCell}>
            {_("empty_list")}
          </td>
        </tr>
      );
    }

    return collabData;
  };

  return (
    <>
      {isSending && <div className="lmask"></div>}
      <div className={styles.listContainer}>
        <h3>
          {_("client_file_list")}{" "}
          <span>
            {search !== "" ? (
              <span>({totalFoundResult})</span>
            ) : (
              !isLoading && nbrResult && <span>({nbrResult})</span>
            )}
          </span>
          <i
            onClick={() => setOpenDelegationTypeModal(true)}
            className={classnames(styles["btDType"], "icon-ttp-settings-alt")}
          ></i>
          {/*refresh must be authorised for deg only*/}
          {/* <i
            className={`icon-ttp-refresh ${styles.refresh}`}
            onClick={() => refreshAssociation()}
          />*/}
        </h3>

        <div className={styles.inputSearch}>
          <span className={styles.inputSearch_icon}>
            <i className="icon-ttp-magnifier"></i>
          </span>
          <input
            type="text"
            id="search-go-clients"
            autoComplete="off"
            placeholder="Recherche ..."
            onChange={(e) => handleSearchChange(e.target.value, 1)}
          />
          {search !== "" && (
            <span
              className={styles.inputSearch_close}
              onClick={() => {
                setSearch("");
                prevSearchRef.current = "";
                document.getElementById("search-go-clients").value = "";
              }}
            >
              <i className="icon-ttp-close"></i>
            </span>
          )}
        </div>
      </div>

      {props.page && props.page === "SETTING" && (
        <ul className={styles.tabForms}>
          <li
            onClick={() => {
              props.setCurrentItem(null);
              props.setSideBar("CLIENT_FORM");
            }}
            className={props.sideBar === "CLIENT_FORM" ? styles.active : ""}
          >
            Ajout Manuel
          </li>
          <li
            onClick={() => {
              props.setCurrentItem(null);
              props.setSideBar("CLIENT_IMPORT");
            }}
            className={props.sideBar === "CLIENT_IMPORT" ? styles.active : ""}
          >
            Import (.xls)
          </li>
          {selectedItems.length > 0 && (
            <li>
              <div
                className={styles.delegationAction}
                onClick={() => setOpenDelegationModal(true)}
              >
                {_("delegate")}
              </div>
            </li>
          )}
          {selectedItems.length === 1 && (
            <li>
              <div
                className={styles.delegationAction}
                onClick={() => {
                  if (selectedItems.length === 1) setOpenContactModal(true);
                }}
              >
                {_("manage_contacts")}
              </div>
            </li>
          )}
          {/* <li
            onClick={() => {
              props.setCurrentItem(null);
              props.setSideBar("CLIENT_EXPRESS");
            }}
            className={props.sideBar === "CLIENT_EXPRESS" ? styles.active : ""}
          >
            Ajout Express
          </li> */}
        </ul>
      )}
      <div className={styles.tableContainer}>
        <table className={`${styles.table} unstriped`}>
          <thead>
            <tr>
              <th></th>
              <th>{_("delegations")}</th>
              <th width="30%">
                <span>{_("company_name")}</span>
                <span className={styles.sortBox}>
                  <i
                    className={`icon-ttp-arrow-down ${
                      sort.property === "name" &&
                      sort.dir === "asc" &&
                      styles.iconActive
                    }`}
                    onClick={() => setSort({ property: "name", dir: "asc" })}
                  ></i>
                  <i
                    className={`icon-ttp-arrow-up ${
                      sort.property === "name" &&
                      sort.dir === "desc" &&
                      styles.iconActive
                    }`}
                    onClick={() => setSort({ property: "name", dir: "desc" })}
                  ></i>
                </span>
              </th>
              <th width="30%">
                <span>{_("legal_form")}</span>
                <span className={styles.sortBox}>
                  <i
                    className={`icon-ttp-arrow-down ${
                      sort.property === "legalForm" &&
                      sort.dir === "asc" &&
                      styles.iconActive
                    }`}
                    onClick={() =>
                      setSort({ property: "legalForm", dir: "asc" })
                    }
                  ></i>
                  <i
                    className={`icon-ttp-arrow-up ${
                      sort.property === "legalForm" &&
                      sort.dir === "desc" &&
                      styles.iconActive
                    }`}
                    onClick={() =>
                      setSort({ property: "legalForm", dir: "desc" })
                    }
                  ></i>
                </span>
              </th>
              <th width="10%">{_("language")}</th>
              <th width="30%">
                <span>{_("linked_contact")}</span>
              </th>
              <th>{_("other_contacts")}</th>
              {/* <th></th> */}
            </tr>
          </thead>
          <tbody>
            {isLoading || uploading ? (
              <tr>
                <td colSpan={7} className={styles.centerCell}>
                  <Loader
                    style={{
                      height: "10px",
                    }}
                    color={"#B2BCC6"}
                  />
                </td>
              </tr>
            ) : search !== "" ? (
              renderData(foundResult)
            ) : (
              renderData(dataGoPeople.folders)
            )}
          </tbody>
        </table>
      </div>
      {nbrResult ? (
        <Pagination
          className="pagination-bar"
          currentPage={currentPage}
          totalCount={search !== "" ? totalFoundResult : nbrResult}
          pageSize={LIMIT}
          onPageChange={(page) => setCurrentPage(page)}
        />
      ) : (
        ""
      )}
      <ModalConfirm
        type="delete"
        isOpen={isOpenDeleteModal}
        onCancel={() => {
          setIsOpenDeleteModal(false);
        }}
        onConfirm={() => handleDeleteAction()}
        inProcess={deleting}
        title={_("delete_confirm")}
        text={_("text_delete_collaborator")}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />
      {openDelegationTypeModal && (
        <ModalDelegationType
          closeModal={() => setOpenDelegationTypeModal(false)}
        />
      )}
      {openDelegationModal && (
        <ModalDelegation
          folderIds={selectedItems}
          folders={data?.data}
          refetchClients={refetch}
          closeModal={() => setOpenDelegationModal(false)}
        />
      )}
      {openContactModal && (
        <ModalContact
          folderIds={selectedItems}
          folders={data?.data}
          refetchClients={refetch}
          closeModal={() => setOpenContactModal(false)}
        />
      )}
    </>
  );
};

export default ClientList;
